'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DateSelectionProps {
  checkIn: string;
  checkOut: string;
  onCheckInChange: (date: string) => void;
  onCheckOutChange: (date: string) => void;
}

export default function DateSelection({
  checkIn,
  checkOut,
  onCheckInChange,
  onCheckOutChange,
}: DateSelectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [nights, setNights] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (checkIn && checkOut) {
      const start = new Date(checkIn);
      const end = new Date(checkOut);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setNights(diffDays);
    }
  }, [checkIn, checkOut]);

  if (!isHydrated) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-display font-semibold text-[var(--color-text-primary)] mb-2">
            Select Your Dates
          </h2>
          <p className="text-[var(--color-text-secondary)]">Choose your check-in and check-out dates</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="h-24 bg-[var(--color-muted)] rounded-lg animate-pulse" />
          <div className="h-24 bg-[var(--color-muted)] rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const minCheckOut = checkIn || today;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display font-semibold text-[var(--color-text-primary)] mb-2">
          Select Your Dates
        </h2>
        <p className="text-[var(--color-text-secondary)]">Choose your check-in and check-out dates</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Check-in Date
          </label>
          <div className="relative">
            <input
              type="date"
              value={checkIn}
              onChange={(e) => onCheckInChange(e.target.value)}
              min={today}
              className="w-full px-4 py-3 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all duration-300 cursor-pointer"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Check-out Date
          </label>
          <div className="relative">
            <input
              type="date"
              value={checkOut}
              onChange={(e) => onCheckOutChange(e.target.value)}
              min={minCheckOut}
              className="w-full px-4 py-3 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all duration-300 cursor-pointer"
            />
          </div>
        </div>
      </div>

      {nights > 0 && (
        <div className="bg-[var(--color-muted)] rounded-lg p-4 flex items-center gap-3">
          <Icon name="InformationCircleIcon" size={24} className="text-[var(--color-primary)]" />
          <div>
            <p className="text-sm font-medium text-[var(--color-text-primary)]">
              Total Stay: {nights} {nights === 1 ? 'night' : 'nights'}
            </p>
            <p className="text-xs text-[var(--color-text-secondary)] mt-1">
              Extended stays (30+ nights) qualify for special corporate rates
            </p>
          </div>
        </div>
      )}
    </div>
  );
}